import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { courseUrl } from '../utils/catalogUrls';
import analyticsService from '../services/analyticsService';
import '../styles/SearchBar.css';

const SearchBar = ({ onFilter, variant = 'default' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const analyticsDebounceRef = useRef(null);
  const searchControllerRef = useRef(null);
  const navigate = useNavigate();

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (analyticsDebounceRef.current) clearTimeout(analyticsDebounceRef.current);
    searchControllerRef.current?.abort();
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) {
      searchControllerRef.current?.abort();
      searchControllerRef.current = null;
      setResults(null);
      setIsOpen(false);
      setLoading(false);
      if (onFilter) onFilter(null);
      return;
    }

    searchControllerRef.current?.abort();
    const controller = new AbortController();
    searchControllerRef.current = controller;
    setLoading(true);
    try {
      const resp = await apiService.search(q, { signal: controller.signal });
      const data = resp.data;
      setResults(data);
      setIsOpen(true);
      const resultCount = (data.cursos?.length || 0)
        + (data.relacionados?.length || 0)
        + (data.categorias?.length || 0);
      if (analyticsDebounceRef.current) clearTimeout(analyticsDebounceRef.current);
      analyticsDebounceRef.current = setTimeout(() => {
        analyticsService.trackSearch(
          q,
          data.match_found === false ? 0 : resultCount,
          data.match_found === false ? 'search_dropdown_fallback' : 'search_dropdown',
        );
      }, 800);
    } catch {
      // A troca do texto cancela buscas antigas de propósito.
    } finally {
      if (searchControllerRef.current === controller) {
        searchControllerRef.current = null;
        setLoading(false);
      }
    }
  }, [onFilter]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    searchControllerRef.current?.abort();
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleCursoClick = (curso) => {
    setIsOpen(false);
    setQuery('');
    navigate(courseUrl(curso));
  };

  const handleCategoriaClick = (categoria) => {
    setIsOpen(false);
    if (onFilter) {
      setQuery(categoria.nome);
      onFilter(categoria);
    }
  };

  const handleClear = () => {
    searchControllerRef.current?.abort();
    searchControllerRef.current = null;
    setLoading(false);
    setQuery('');
    setResults(null);
    setIsOpen(false);
    if (onFilter) onFilter(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const totalResults = results
    ? (results.cursos?.length || 0) + (results.relacionados?.length || 0)
    : 0;

  return (
    <div className={`searchbar-wrapper searchbar-${variant}`} ref={wrapperRef}>
      <div className="searchbar-input-wrapper">
        <svg className="searchbar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
          <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          className="searchbar-input"
          placeholder="Buscar cursos por nome ou categoria..."
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results && setIsOpen(true)}
          aria-label="Buscar cursos"
          autoComplete="off"
        />
        {loading && <div className="searchbar-spinner" />}
        {query && !loading && (
          <button className="searchbar-clear" onClick={handleClear} aria-label="Limpar busca">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        )}
      </div>

      {isOpen && results && (
        <div className="searchbar-dropdown">
          {results.match_found === false && (
            <div className="searchbar-suggestion-message" role="status">
              {results.suggestion_message || 'Curso não encontrado, mas você pode gostar desses cursos!'}
            </div>
          )}

          {results.categorias?.length > 0 && (
            <div className="searchbar-section">
              <div className="searchbar-section-title">
                {results.match_found === false ? 'Categorias relacionadas' : 'Categorias'}
              </div>
              {results.categorias.map((cat) => (
                <button
                  key={`cat-${cat.id}`}
                  className="searchbar-item searchbar-cat-item"
                  onClick={() => handleCategoriaClick(cat)}
                >
                  <span className="searchbar-cat-dot" style={{ background: cat.cor }} />
                  <span className="searchbar-cat-name">{cat.nome}</span>
                  <span className="searchbar-cat-count">{cat.cursos_count} cursos</span>
                </button>
              ))}
            </div>
          )}

          {results.cursos?.length > 0 && (
            <div className="searchbar-section">
              <div className="searchbar-section-title">Cursos</div>
              {results.cursos.map((curso) => (
                <button
                  key={`curso-${curso.id}`}
                  className="searchbar-item searchbar-curso-item"
                  onClick={() => handleCursoClick(curso)}
                  data-analytics-course-id={curso.id}
                  data-analytics-source="search_dropdown"
                >
                  {curso.imagem_url && (
                    <img
                      src={curso.imagem_url}
                      alt=""
                      className="searchbar-curso-thumb"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="searchbar-curso-info">
                    <span className="searchbar-curso-title">{curso.title}</span>
                    {curso.categorias?.length > 0 && (
                      <span className="searchbar-curso-cats">
                        {curso.categorias.map(c => c.nome).join(', ')}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.relacionados?.length > 0 && (
            <div className="searchbar-section">
              <div className="searchbar-section-title">
                {results.match_found === false ? 'Cursos que você pode gostar' : 'Cursos relacionados'}
              </div>
              {results.relacionados.slice(0, 5).map((curso) => (
                <button
                  key={`rel-${curso.id}`}
                  className="searchbar-item searchbar-curso-item"
                  onClick={() => handleCursoClick(curso)}
                  data-analytics-course-id={curso.id}
                  data-analytics-source="search_dropdown"
                >
                  {curso.imagem_url && (
                    <img
                      src={curso.imagem_url}
                      alt=""
                      className="searchbar-curso-thumb"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="searchbar-curso-info">
                    <span className="searchbar-curso-title">{curso.title}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {totalResults === 0 && results.categorias?.length === 0 && (
            <div className="searchbar-empty">
              Nenhum resultado encontrado para "<strong>{query}</strong>"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
